import { Link as RouterLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import media from '../../constants/media';
import theme from '../../constants/theme';
import { pageContainerSpacing } from '../../constants/theme/theme';
import zIndex from '../../constants/zIndex';
import { ReactComponent as IconChevronLeft } from '../../icons/chevron-left-rounded.svg';
import LinkWithIcon from '../base/linkWithIcon';
import { PageContainer } from '../base/pageContainer/pageContainer';
import GrayTextLink from '../grayTextLink';
import LocaleSwitcher from '../localeSwitcher';
import TextLinkWithArrow from '../textLinkWithArrow';

export const HeaderContainer = styled.div`
	padding-block: 8px 15px;
	font-family: ${theme.fonts.Arimo};
	letter-spacing: 0;

	${media.desktopSmall`
		padding-block: 24px;
	`}
`;

export const PageContainerStyled = styled(PageContainer)`
	padding-inline: ${pageContainerSpacing.sm}px 0;

	${media.tablet`
		padding-inline: ${pageContainerSpacing.md}px 0;
	`}

	${media.desktopSmall`
		padding-inline: ${pageContainerSpacing.lg}px 0;
	`}
`;

export const NavigationWrapper = styled.nav`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

export const PageOverlay = styled.div`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	background-color: ${theme.colorsV2.mainDark}20;
	z-index: ${zIndex.pageOverlay};
`;

export const PrimaryMenu = styled.div`
	display: flex;
`;

export const PrimaryButton = styled.button`
	display: inline-block;
	position: relative;
	z-index: ${zIndex.header};
	font-family: inherit;
	font-size: 18px;
	padding: 8px;
	line-height: 1.4;
	cursor: pointer;
	background-color: transparent;
	color: ${theme.colorsV2.mainDark};
	border-radius: 8px;
	transition: color 200ms ease, background-color 200ms ease;

	&:not(:first-of-type) {
		margin-left: 32px;
	}

	&::before {
		display: block;
		content: attr(aria-label);
		font-weight: ${theme.fonts.weight.medium};
		height: 0;
		overflow: hidden;
		visibility: hidden;
	}

	&[aria-expanded='true'],
	&:hover {
		font-weight: ${theme.fonts.weight.medium};
		color: ${theme.colorsV2.mainRed};
		background-color: ${theme.colors.white};
	}
`;

export const IconChevronLeftStyled = styled(IconChevronLeft)`
	transition: all 0.2s ease;
	path {
		stroke: ${theme.colorsV2.mainRed};
	}
`;

export const MobileAccordion = styled.div`
	width: 100%;
`;

export const MobileAccordionDropdown = styled.div`
	max-height: 0;
	overflow: hidden;
	transition: max-height 0.3s ease;
`;

export const MobileAccordionHeader = styled.button<{ expanded: boolean }>`
	margin-top: 32px;
	display: inline-flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	font-family: inherit;
	cursor: pointer;
	background-color: transparent;

	&[aria-expanded='true'] {
		svg {
			transform: rotate(-90deg);
		}

		& + ${MobileAccordionDropdown} {
			max-height: 1050px;
		}
	}
`;

export const MobileAccordionHeaderText = styled.h5``;

export const SearchLink = styled(LinkWithIcon)`
	margin-left: 40px;
	position: relative;
	z-index: ${zIndex.header};

	&:hover {
		font-weight: ${theme.fonts.weight.medium};
	}
`;

export const MobileSearchLink = styled(LinkWithIcon)`
	justify-content: space-between;
	flex-direction: row-reverse;
	margin-top: 32px;
	width: 100%;

	svg {
		margin: 0;
		path {
			stroke: ${theme.colorsV2.mainRed};
		}
	}
`;

export const SecondaryMenu = styled.div`
	display: flex;
	align-items: center;
`;

export const MyAccountLink = styled(LinkWithIcon)`
	padding: 8px;
	font-weight: ${theme.fonts.weight.medium};
	color: ${theme.colorsV2.mainRed};
	background-color: ${theme.colorsV2.grey1};
	border-radius: 8px;

	svg path {
		stroke: ${theme.colorsV2.mainRed};
	}

	&:hover {
		background-color: ${theme.colors.white};
	}

	${media.desktopSmall`
		position: relative;
		z-index: ${zIndex.header};
		background-color: transparent;
	`}
`;

export const LanguageSwitch = styled(LocaleSwitcher)`
	position: relative;
	z-index: ${zIndex.headerLogo};

	${media.desktopSmall`
		margin-left: 32px;
	`}
`;

export const LogoWrapper = styled(RouterLink)`
	margin-left: 32px;
	width: 100px;
	height: 60px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	z-index: ${zIndex.headerLogo};
	border-radius: 15px 0 0 15px;
	text-decoration: none;

	img,
	svg {
		height: auto;
		width: auto;
		transform: translateY(1.5rem);

		path {
			fill: ${theme.colors.white};
		}

		.cls-1 {
			stroke: ${theme.colors.white};
			stroke-width: 0.6px;
			paint-order: stroke fill;
		}

		.cls-2 {
			fill: ${theme.colorsV2.mainRed};
			stroke: ${theme.colorsV2.mainRed};
		}
	}

	&::after {
		content: '';
		position: absolute;
		bottom: -30px;
		right: 0;
		z-index: -1;
		height: 30px;
		width: 15px;
		border-top-right-radius: 15px;
	}

	${media.desktopSmall`
		width: 128px;
		height: 80px;
		border-radius: 20px 0 0 20px;
	`}
`;

export const NavigationDropdown = styled.div`
	display: flex;
	flex-direction: column;
	overflow-y: auto;

	${media.desktopSmall`
		position: absolute;
		z-index: ${zIndex.header};
		max-width: calc(100vw - 80px);
		border-radius: 16px;
		box-shadow: 0px 4px 57px #8F8F8F25;
		overflow: hidden;
	`}
`;

export const MenuItemWrapper = styled.div<{ mobileMenuOpen: boolean }>`
	${({ mobileMenuOpen }) =>
		mobileMenuOpen &&
		css`
			position: fixed;
			top: 0;
			left: 0;
			z-index: ${zIndex.header};
			padding-top: 83px;
			width: 100%;
			height: 100%;
			background-color: ${theme.colors.white};

			${NavigationDropdown} {
				position: relative;
				height: 100%;
			}
		`}
`;

export const DropdownTopSection = styled.div`
	display: flex;
	align-items: flex-start;
	flex-direction: column;
	padding: 16px 16px 56px;
	background-color: ${theme.colors.white};
	flex: 1 0 auto;

	${MobileSearchLink},
	${MobileAccordionHeaderText} {
		font-family: ${theme.fonts.SharpSlab};
		font-weight: ${theme.fonts.weight.semiBold};
		font-size: 32px;
		letter-spacing: -0.04em;
		line-height: 1.2;
		color: ${theme.colorsV2.mainRed};
	}

	${media.tablet`
		padding: 16px 32px 56px;
	`}

	${media.desktopSmall`
		flex-direction: row;
		padding: 88px 48px;
	`}
`;

export const DropdownPrimaryColumn = styled.div`
	width: 100%;
	max-width: 345px;
`;

export const TextLinkWithArrowStyled = styled(TextLinkWithArrow)`
	margin-top: 12px;

	${media.desktopSmall`
		margin-top: 16px;
	`}
`;

export const DropdownSecondaryColumnWrapper = styled.div`
	margin-left: 104px;
	display: flex;
`;

export const DropdownSecondaryColumn = styled.div`
	margin-top: 32px;
	width: 100%;

	${media.desktopSmall`
		margin-top: 0;
		max-width: 274px;

		&:not(:first-of-type) {
			margin-left: 32px;
		}
	`}

	@media only screen and (min-width: 1200px) {
		width: 274px;
	}
`;

export const ColumnTitle = styled.h6`
	margin-bottom: 16px;
	font-size: 18px;
	font-weight: ${theme.fonts.weight.medium};
	line-height: 1.2;
	color: ${theme.colorsV2.mainDark};
`;

export const LinkWrapper = styled.div`
	&:not(:last-of-type) {
		margin-bottom: 16px;

		${media.desktopSmall`
			margin-bottom: 8px;
		`}
	}
`;

export const Link = styled(GrayTextLink)`
	margin-bottom: 8px;
	line-height: 1.2;
	color: ${theme.colorsV2.grey7};
	font-size: 18px;
`;

export const LogoutLink = styled.button`
	margin-top: 8px;
	display: flex;
	color: ${theme.colorsV2.mainRed};
	font-family: inherit;
	font-weight: ${theme.fonts.weight.medium};
	font-size: 18px;
	line-height: 1.4;
	background-color: transparent;
`;

export const DropdownBottomSection = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	padding: 24px 16px;
	background-color: ${theme.colorsV2.grey2};

	${media.tablet`
		padding: 24px 32px;
	`}

	${media.desktopSmall`
		flex-direction: row;
		justify-content: space-between;
		padding: 16px 24px;
	`}
`;

export const BottomLink = styled(LinkWithIcon)`
	margin-block: 8px;
	flex: 0 0 auto;
	color: ${theme.colorsV2.grey7};

	svg path {
		stroke: ${theme.colorsV2.grey7};
	}
`;

export const DropdownBottomStatic = styled.div`
	display: flex;
	justify-content: center;
	align-items: flex-start;
	flex-wrap: wrap;
	margin-inline: -8px;

	${BottomLink} {
		margin-inline: 8px;

		${media.desktopSmall`
			margin-inline: 16px;
		`}
	}

	${media.desktopSmall`
		align-items: center;
		margin-inline: -16px;
	`}
`;

export const DropdownBottomExtraLink = styled(MyAccountLink)`
	position: static;
	background-color: ${theme.colorsV2.grey1};

	${media.desktopSmall`
		margin-left: 32px;
	`}
`;

export const DropdownBottomDynamic = styled.div`
	display: flex;
	justify-content: center;
	align-items: flex-start;
	flex-wrap: wrap;
	margin-inline: -8px;

	${MyAccountLink},
	${DropdownBottomExtraLink} {
		margin: 8px;
	}
`;

export const MenuButtonCenter = styled.span`
	margin-inline: 3px;
	display: flex;
	width: 26px;
	height: 2px;
	background-color: ${theme.colorsV2.mainDark};
	border-radius: 2px;
`;

export const BurgerMenuButton = styled.button`
	position: relative;
	z-index: ${zIndex.headerLogo};
	width: 32px;
	height: 32px;
	background-color: transparent;
	cursor: pointer;

	&::before,
	&::after {
		content: '';
		position: absolute;
		left: 3px;
		width: 26px;
		height: 2px;
		background-color: ${theme.colorsV2.mainDark};
		border-radius: 2px;
		transition: all 0.2s ease;
	}
	&::before {
		top: 5px;
	}
	&::after {
		bottom: 5px;
	}

	&[aria-expanded='true'] {
		${MenuButtonCenter} {
			display: none;
		}
		&::before,
		&::after {
			top: auto;
			bottom: auto;
		}
		&::before {
			transform: rotateZ(45deg);
		}
		&::after {
			transform: rotateZ(-45deg);
		}
	}
`;

export const MyAccountDropdownContent = styled.div`
	display: none;
	position: absolute;
	padding: 20px;
	border-radius: 8px;
	box-shadow: 0px 4px 57px #8f8f8f25;
	z-index: 12;
	box-sizing: border-box;
`;

export const MyAccountDropDownLink = styled.a`
	display: flex;
	justify-content: space-between;
	font-size: 18px;
	line-height: 1.4;
	text-decoration: none;
	color: ${theme.colorsV2.mainDark};

	span {
		padding-right: 20px;
		white-space: nowrap;
	}

	&:hover {
		color: ${theme.colorsV2.mainRed};
	}

	&:not(:last-child) {
		margin-bottom: 20px;
	}

	svg {
		position: relative;
		width: 24px;
		height: 24px;

		path {
			stroke: ${theme.colorsV2.mainRed};
		}

		fill: ${theme.colorsV2.mainRed};
	}
`;

export const MyAccountDropdownMenu = styled.div`
	position: relative;
	display: inline-block;

	&:hover ${MyAccountDropdownContent} {
		display: block;
		background-color: white;
	}
`;

export const MyAccountDropdownHeader = styled.div`
	position: relative;
	color: ${theme.colorsV2.mainRed};
	padding-bottom: 10px;

	svg {
		position: relative;
		top: 5px;
		margin-right: 8px;

		path {
			stroke: ${theme.colorsV2.mainRed};
		}
	}
`;

export const CloseMenuButton = styled.button`
	background: none;
	border: none;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	cursor: pointer;
	width: 32px;
	height: 32px;
	z-index: 20;
`;
